import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Alert,
  Box,
  Paper,
} from '@mui/material';
import { useListPsiOpportunityInterests } from '@/lib/api/opportunity-interests.ts';
import { InvestorList } from './InvestorList.tsx';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface SelectInvestorModalProps {
  open: boolean;
  onClose: () => void;
  opportunityId: string;
  onSelectInvestor: (investorId: string) => void;
}

const SelectInvestorModal = ({
  open,
  onClose,
  opportunityId,
  onSelectInvestor,
}: SelectInvestorModalProps) => {
  const [selectedInterestId, setSelectedInterestId] = useState<string | null>(null);
  const { data: interests, isLoading } = useListPsiOpportunityInterests(opportunityId);

  const handleSelectInterest = (interestId: string) => {
    setSelectedInterestId(interestId);
  };

  const handleClose = () => {
    setSelectedInterestId(null);
    onClose();
  };

  const handleConfirm = () => {
    if (selectedInterestId) {
      onSelectInvestor(selectedInterestId);
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Seleccionar inversor
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Elige el inversor con quien deseas cerrar esta oportunidad
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          py: 2,
          overflow: 'auto',
        }}
      >
        <Stack spacing={2.5}>
          {/* 24-hour deadline warning */}
          <Alert
            severity="warning"
            icon={<ScheduleIcon />}
            sx={{
              bgcolor: 'warning.lighter',
              '& .MuiAlert-icon': {
                color: 'warning.main',
              },
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Plazo de confirmación: 24 horas
            </Typography>
            <Typography variant="body2">
              Una vez seleccionado, el inversor tendrá <strong>24 horas</strong> para confirmar la
              reserva de la oportunidad. Si no confirma en este plazo, será descartado
              automáticamente y podrás seleccionar otro inversor.
            </Typography>
          </Alert>

          {/* Contact recommendation */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: '2px solid',
              borderColor: 'info.main',
              bgcolor: 'info.lighter',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <PhoneInTalkIcon sx={{ color: 'info.main', fontSize: 28, mt: 0.5 }} />
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight={600} color="info.dark" mb={1}>
                  Recomendación importante
                </Typography>
                <Typography variant="body2" color="text.primary">
                  <strong>Te sugerimos encarecidamente</strong> que contactes previamente con los
                  inversores interesados antes de realizar la selección para confirmar el interés
                  real y solventar cualquier duda.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Investor list section */}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                Selecciona un inversor de la lista
              </Typography>
            </Stack>
            <InvestorList
              interests={interests}
              isLoading={isLoading}
              selectedInterestId={selectedInterestId}
              onSelectInterest={handleSelectInterest}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Button onClick={handleClose} size="large" color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          size="large"
          disabled={!selectedInterestId}
          sx={{ minWidth: 180 }}
        >
          Confirmar selección
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectInvestorModal;
